import React from 'react';
import { useAccessibility } from '../contexts/AccessibilityContext';
import '../styles/SkipLinks.css';

interface SkipLink {
  href: string;
  text: string;
  targetId: string;
}

interface SkipLinksProps {
  links?: SkipLink[];
  className?: string;
}

const defaultLinks: SkipLink[] = [
  {
    href: '#main-content',
    text: 'Skip to main content',
    targetId: 'main-content',
  },
  {
    href: '#navigation',
    text: 'Skip to navigation',
    targetId: 'navigation',
  },
  {
    href: '#music-editor',
    text: 'Skip to music editor',
    targetId: 'music-editor',
  },
];

const SkipLinks: React.FC<SkipLinksProps> = ({
  links = defaultLinks,
  className = '',
}) => {
  const { focusSkipTarget, announceToScreenReader } = useAccessibility();

  const handleSkipLinkClick = (
    event: React.MouseEvent<HTMLAnchorElement>,
    targetId: string,
  ) => {
    event.preventDefault();

    // Try to focus the target element
    const targetElement = document.getElementById(targetId);
    if (targetElement) {
      // Make sure the element can receive focus
      if (targetElement.tabIndex < 0) {
        targetElement.tabIndex = -1;
      }

      targetElement.focus();
      targetElement.scrollIntoView({ behavior: 'smooth', block: 'start' });

      // Announce the skip action
      const linkText =
        links.find(link => link.targetId === targetId)?.text || 'content';
      announceToScreenReader(`Skipped to ${linkText}`, 'assertive');
    } else {
      // Fallback: try to focus using the accessibility context
      focusSkipTarget(targetId);
    }
  };

  const handleKeyDown = (
    event: React.KeyboardEvent<HTMLAnchorElement>,
    targetId: string,
  ) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleSkipLinkClick(event as any, targetId);
    }
  };

  return (
    <nav
      className={`skip-links ${className}`}
      role="navigation"
      aria-label="Skip links navigation"
    >
      <h2 className="sr-only">Skip Links</h2>
      <ul className="skip-links__list">
        {links.map((link, index) => (
          <li key={index} className="skip-links__item">
            <a
              href={link.href}
              className="skip-links__link"
              onClick={event => handleSkipLinkClick(event, link.targetId)}
              onKeyDown={event => handleKeyDown(event, link.targetId)}
              aria-describedby={`skip-desc-${index}`}
            >
              {link.text}
            </a>
            <span id={`skip-desc-${index}`} className="sr-only">
              Press Enter to jump to {link.text.toLowerCase()}
            </span>
          </li>
        ))}
      </ul>
    </nav>
  );
};

export default SkipLinks;
